import { type CreateMultipartResponse, type UploadUrlResponse } from '@air/api/types';
import { type Upload } from '@air/redux-uploader';

import { BaseUploadUrlsManager, type ShouldBatchTogetherParams } from '~/components/Upload/utils/BaseUploadUrlsManager';
import { getUploadBoardIdFromStore } from '~/components/Upload/utils/getUploadBoardIdFromStore';
import { metadataForPrivateUploadSelector } from '~/store/privateUploadMetadata/selectors';
import { type PrivateUploadMetadata } from '~/store/privateUploadMetadata/types';
import { sendCreatePrivateMultipartUpload } from '~/utils/uploadApi/privateUploadApi/privateLargeUploadApi';
import { fetchSmallPrivateUploadUrl } from '~/utils/uploadApi/privateUploadApi/privateSmallUploadApi';

export class GetPrivateUploadUrlsManager extends BaseUploadUrlsManager<PrivateUploadMetadata> {
  protected getUploadMetadata(uploadId: string): PrivateUploadMetadata | undefined {
    return metadataForPrivateUploadSelector(this.store.getState(), uploadId);
  }

  protected shouldBatchTogether({
    requestedUpload,
    otherUpload,
    requestedMetadata,
    otherMetadata,
  }: ShouldBatchTogetherParams<PrivateUploadMetadata>): boolean {
    const requestedParentBoardId = getUploadBoardIdFromStore({ upload: requestedUpload, store: this.store });
    const otherParentBoardId = getUploadBoardIdFromStore({ upload: otherUpload, store: this.store });

    return (
      requestedMetadata.workspaceId === otherMetadata.workspaceId &&
      requestedMetadata.assetId === otherMetadata.assetId &&
      requestedParentBoardId === otherParentBoardId
    );
  }

  protected fetchSmallUploadUrls(uploads: Upload[], metadata: PrivateUploadMetadata): Promise<UploadUrlResponse> {
    return fetchSmallPrivateUploadUrl({
      workspaceId: metadata.workspaceId,
      assetId: metadata.assetId,
      parentBoardId: getUploadBoardIdFromStore({ upload: uploads[0], store: this.store }),
      apiUploadData: uploads.map((u) => u.apiUploadData),
    });
  }

  protected fetchLargeUploadUrls(uploads: Upload[], metadata: PrivateUploadMetadata): Promise<CreateMultipartResponse> {
    return sendCreatePrivateMultipartUpload({
      workspaceId: metadata.workspaceId,
      assetId: metadata.assetId,
      parentBoardId: getUploadBoardIdFromStore({ upload: uploads[0], store: this.store }),
      apiUploadData: uploads.map((u) => u.apiUploadData),
    });
  }
}
